import React from "react";
import {
  NavigationStepItem,
  NavigationSteps
} from "../../../shared/components/Layout/NavigationSteps";
import { IFormWizardPage } from "../selectors/selectorWizardPages";

type Props = {
  pages: IFormWizardPage[];
  activePageIndex: number;
  onPageClick?: (index: number) => void;
};

type RailEntry = { page: IFormWizardPage; index: number };
type RailCard =
  | { type: "page"; entry: RailEntry }
  | { type: "group"; entries: RailEntry[] };

// Groups the flat, ordered page list into the rail's cards: each top-level
// page is its own card, and every run of consecutive sub-pages collapses into
// a single "Form sections" card that lists them as dot sub-items.
const groupRailCards = (pages: IFormWizardPage[]): RailCard[] => {
  const cards: RailCard[] = [];
  pages.forEach((page, index) => {
    const entry: RailEntry = { page, index };
    if (page.kind === "top") {
      cards.push({ type: "page", entry });
      return;
    }
    const last = cards[cards.length - 1];
    if (last && last.type === "group") {
      last.entries.push(entry);
    } else {
      cards.push({ type: "group", entries: [entry] });
    }
  });
  return cards;
};

/**
 * Left navigation rail for the dynamic form wizard — rendered with the shared
 * Layout `NavigationSteps` rail (the controlled variant of the flow
 * `Navigation` used by onboarding), so the form wizard looks identical to the
 * app's existing flow navigation. Each top-level page is a rail step (the
 * form's identity page first, "Sign and date" last) and every run of section
 * sub-pages collapses into one "Form sections" step whose sub-items show while
 * it is active. Completed steps are clickable to jump back — driven by the
 * form's local `activePageIndex` (the form is one page that swaps which wizard
 * page's sections are visible).
 */
export const DynamicFormStepper: React.FC<Props> = ({
  pages,
  activePageIndex,
  onPageClick
}) => {
  const cards = groupRailCards(pages);

  const steps: NavigationStepItem[] = cards.map(card =>
    card.type === "page"
      ? {
          key: card.entry.page.key,
          title: card.entry.page.title,
          hasWarning: card.entry.page.hasWarning
        }
      : {
          key: `sections-${card.entries[0].page.key}`,
          title: "Form sections",
          hasWarning: card.entries.some(({ page }) => page.hasWarning),
          steps: card.entries.map(({ page }) => ({
            key: page.key,
            title: page.title,
            hasWarning: page.hasWarning
          }))
        }
  );

  const activeCardIndex = cards.findIndex(card =>
    card.type === "page"
      ? card.entry.index === activePageIndex
      : card.entries.some(({ index }) => index === activePageIndex)
  );
  const activeCard = cards[activeCardIndex];
  const activeSubIndex =
    activeCard && activeCard.type === "group"
      ? activeCard.entries.findIndex(({ index }) => index === activePageIndex)
      : undefined;

  return (
    <NavigationSteps
      steps={steps}
      activeIndex={activeCardIndex}
      activeSubIndex={activeSubIndex}
      onStepClick={
        onPageClick
          ? (stepIndex, subStepIndex) => {
              const card = cards[stepIndex];
              if (!card) return;
              const pageIndex =
                card.type === "page"
                  ? card.entry.index
                  : card.entries[subStepIndex ?? 0]?.index;
              if (pageIndex !== undefined) onPageClick(pageIndex);
            }
          : undefined
      }
    />
  );
};
